import { Box, Typography, Button, Divider, Chip, Avatar, IconButton } from '@mui/material';
import { Building2, MapPin, Users, Activity, Eye, LayoutDashboard, Shuffle, MoreVertical, Wallet } from 'lucide-react';
import { FcLibrary } from 'react-icons/fc';
import { tokens } from '@/theme/tokens';
import { useNavigate } from 'react-router-dom';
import { mockSchoolSummaries } from '@/mock/schools';

export function SchoolCard({ school, onSwitch, onCompare, onEdit }) {
  const navigate = useNavigate();
  
  const statusColors = {
    active: 'success',
    inactive: 'error',
    maintenance: 'warning',
  };

  const summary = mockSchoolSummaries[school.id];
  const thisMonthCollection = summary ? summary.thisMonthCollection : 0;
  const formattedCollection = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(thisMonthCollection);

  return (
    <Box
      sx={{
        bgcolor: tokens.colors.card,
        borderRadius: 3,
        border: `1px solid ${tokens.colors.border}`,
        overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: tokens.shadows.card,
        },
      }}
    >
      {/* Header / Banner Area */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative' }}>
        <Box 
          sx={{
            width: 64,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: tokens.colors.primary[50],
            borderRadius: 2
          }}
        >
          <FcLibrary size={42} />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {school.name}
            </Typography>
            <Chip 
              label={school.status} 
              size="small" 
              color={statusColors[school.status] || 'default'}
              sx={{ textTransform: 'capitalize', fontWeight: 600, height: 24, fontSize: '0.75rem' }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Building2 size={14} /> {school.code} • {school.schoolType}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Info Body */}
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MapPin size={16} color={tokens.colors.text.muted} />
          <Typography variant="body2" color="text.secondary">
            {school.city}, {school.state}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Wallet size={16} color={tokens.colors.text.muted} />
          <Typography variant="body2" color="text.secondary">
            {formattedCollection} Collection This Month
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Activity size={16} color={tokens.colors.text.muted} />
          <Typography variant="body2" color="text.secondary">
            Principal: {school.principalName}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Actions */}
      <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1, bgcolor: tokens.colors.background, mt: 'auto' }}>
        <Button 
          variant="contained" 
          size="small"
          startIcon={<LayoutDashboard size={14} />}
          onClick={() => onSwitch(school.id)}
          sx={{ flex: 1, minWidth: '40%', fontSize: '0.75rem', py: 1 }}
          disableElevation
        >
          Dashboard
        </Button>
        <Button 
          variant="outlined" 
          size="small"
          startIcon={<Eye size={14} />}
          onClick={() => navigate(`/schools/${school.id}`)}
          sx={{ flex: 1, minWidth: '40%', fontSize: '0.75rem', py: 1 }}
        >
          Details
        </Button>
      </Box>
    </Box>
  );
}
